import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Stack
} from '@mui/material';

import { FleetSettings, VehicleSettings, RideSchema, FleetPresetType, BreakPeriod, VehicleShift } from '../../types/simulation';
import { generateSyntheticFleet, generateIntelligentFleet, analyzeTripsPerHour, FLEET_PRESETS, generateFleetWithPreset, applyFleetPreset } from '../../services/simulation/syntheticData';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { extractServiceDate, combineDateWithTime } from '../../utils/dateTimeUtils';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
interface VehicleCreatorProps {
  onFleetCreated: (fleet: FleetSettings) => void;
  fleet: FleetSettings | null;
  rides: RideSchema[];
  selectedFleetPresets?: FleetPresetType[];
  onFleetPresetSelectionChange?: (selectedPresets: FleetPresetType[]) => void;
}

interface VehicleForm {
  vehicleType: 'standard' | 'wheelchair';
  standardSeats: number;
  wheelchairSeats: number;
  numberOfVehicles: number;
  brand: string;
  startLat: number;
  startLng: number;
  endLat: number;
  endLng: number;
  startTime: string;
  endTime: string;
}

const defaultVehicleForm: VehicleForm = {
  vehicleType: 'standard',
  standardSeats: 12,
  wheelchairSeats: 0,
  numberOfVehicles: 1,
  brand: 'Default Brand',
  startLat: 51.5074, // London center
  startLng: -0.1278,
  endLat: 51.5074,
  endLng: -0.1278,
  startTime: '06:00:00',
  endTime: '22:00:00'
};

// Excel/CSV parsing functions for fleet import
interface VehicleRowData {
  'Vehicle Name'?: string;
  'Brand'?: string;
  'Vehicle Type'?: string;
  'Capacity (Standard)'?: number;
  'Capacity (Wheelchair)'?: number;
  'Number of Vehicles'?: number;
  'Shift #'?: number;
  'Shift Start'?: string;
  'Shift End'?: string;
  'Start Lat'?: number;
  'Start Lon'?: number;
  'End Lat'?: number;
  'End Lon'?: number;
  'Break #'?: number;
  'Break Start'?: string;
  'Break End'?: string;
  'Break Duration (s)'?: number;
  'Break Lat'?: number;
  'Break Lon'?: number;
}

function parseVehicleDataForSimulation(data: VehicleRowData[]): FleetSettings {
  const vehicleMap = new Map<string, VehicleSettings>();
  
  data.forEach((row, index) => {
    // Skip empty rows
    if (!row['Vehicle Name'] && !Object.values(row).some(v => v !== undefined && v !== '')) {
      return;
    }
    
    const vehicleName = row['Vehicle Name'] || `Imported Vehicle ${index + 1}`;
    const vehicleId = `imported-${Date.now()}-${Math.random()}-${vehicleName.replace(/\s+/g, '-').toLowerCase()}`;
    
    if (!vehicleMap.has(vehicleId)) {
      // Parse time from various formats
      const parseTime = (timeStr: string): string => {
        if (!timeStr) return '06:00:00';
        
        // Handle Excel time format, ISO format, or simple time format
        if (timeStr.includes('T')) {
          // ISO format like "2025-08-2T06:00:00"
          const timePart = timeStr.split('T')[1]?.split('.')[0] || timeStr;
          return timePart.length === 8 ? timePart : `${timePart}:00`;
        } else if (timeStr.includes(' ')) {
          // Format like "2025-08-2 06:00:00"
          const timePart = timeStr.split(' ')[1] || timeStr;
          return timePart.length === 8 ? timePart : `${timePart}:00`;
        } else if (timeStr.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
          // Simple time format like "06:00" or "06:00:00"
          return timeStr.length === 5 ? `${timeStr}:00` : timeStr;
        }
        return `${timeStr}:00`;
      };
      
      // Create new vehicle in simulation format
      const vehicleSettings: VehicleSettings = {
        number_of_vehicles: Number(row['Number of Vehicles']) || 1,
        vehicle: {
          capacity: [
            { type: 'standard', number: Number(row['Capacity (Standard)']) || 0 },
            { type: 'wheelchair', number: Number(row['Capacity (Wheelchair)']) || 0 }
          ],
          brand: row['Brand'] || 'Imported Brand',
          vehicle_type: row['Vehicle Type'] || 'standard',
          shifts: []
        }
      };
      
      // Handle shifts
      const shiftStart = row['Shift Start'];
      const shiftEnd = row['Shift End'];
      
      if (shiftStart && shiftEnd) {
        const shift: VehicleShift = {
          start_address: {
            latitude: Number(row['Start Lat']) || 51.5074,
            longitude: Number(row['Start Lon']) || -0.1278
          },
          end_address: {
            latitude: Number(row['End Lat']) || 51.5074,
            longitude: Number(row['End Lon']) || -0.1278
          },
          start_time: combineDateWithTime(DEFAULT_SIMULATION_DATE, parseTime(shiftStart).slice(0, 5)),
          end_time: combineDateWithTime(DEFAULT_SIMULATION_DATE, parseTime(shiftEnd).slice(0, 5)),
          breaks: [] as BreakPeriod[]
        };
        
        // Handle breaks
        const breakStart = row['Break Start'];
        const breakEnd = row['Break End'];
        const breakDuration = row['Break Duration (s)'];
        
        if (breakStart && breakEnd) {
          // Use break coordinates if provided, otherwise default to start location
          const breakLat = Number(row['Break Lat']) || shift.start_address.latitude;
          const breakLon = Number(row['Break Lon']) || shift.start_address.longitude;
          
          const breakPeriod: BreakPeriod = {
            start: parseTime(breakStart),
            end: parseTime(breakEnd),
            location: `${breakLat},${breakLon}`
          };
          shift.breaks = [breakPeriod];
        }
        
        vehicleSettings.vehicle.shifts = [shift];
      }
      
      vehicleMap.set(vehicleId, vehicleSettings);
    }
  });
  
  return {
    vehicles: Array.from(vehicleMap.values())
  };
}

function parseExcelFileForSimulation(file: File): Promise<FleetSettings> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as VehicleRowData[];
        
        const fleet = parseVehicleDataForSimulation(jsonData);
        resolve(fleet);
      } catch (error) {
        reject(new Error(`Error parsing Excel file: ${error}`));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsArrayBuffer(file);
  });
}

function parseCSVFileForSimulation(file: File): Promise<FleetSettings> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (results.errors.length > 0) {
            console.warn('CSV parsing warnings:', results.errors);
          }
          const fleet = parseVehicleDataForSimulation(results.data as VehicleRowData[]);
          resolve(fleet);
        } catch (error) {
          reject(new Error(`Error parsing CSV file: ${error}`));
        }
      },
      error: (error) => {
        reject(new Error(`Error reading CSV file: ${error}`));
      }
    });
  });
}

export const VehicleCreator: React.FC<VehicleCreatorProps> = ({
  onFleetCreated,
  fleet,
  rides,
  selectedFleetPresets = [],
  onFleetPresetSelectionChange
}) => {
  const { t } = useTranslation('simulation');
  const [vehicleForm, setVehicleForm] = useState<VehicleForm>(defaultVehicleForm);
  const [vehicles, setVehicles] = useState<VehicleSettings[]>(fleet?.vehicles || []);
  const [error, setError] = useState<string | null>(null);
  const [showAdvancedFeatures, setShowAdvancedFeatures] = useState(false);
  const [tripAnalysis, setTripAnalysis] = useState<{ maxTripsPerHour: number; hourlyDistribution: { [hour: string]: number } } | null>(null);

  // Fleet preset toggle functionality
  const toggleFleetPreset = (presetType: FleetPresetType) => {
    if (!onFleetPresetSelectionChange) return;
    
    const newSelectedPresets = selectedFleetPresets.includes(presetType)
      ? selectedFleetPresets.filter(p => p !== presetType)
      : [...selectedFleetPresets, presetType];
    
    onFleetPresetSelectionChange(newSelectedPresets);
  };

  // Calculate centroid of ride locations
  const calculateRideCentroid = () => {
    if (rides.length === 0) return { lat: 51.5074, lng: -0.1278 }; // Default to London

    let totalLat = 0;
    let totalLng = 0;
    let count = 0;

    rides.forEach(ride => {
      totalLat += ride.from_latitude + ride.to_latitude;
      totalLng += ride.from_longitude + ride.to_longitude;
      count += 2;
    });

    return {
      lat: totalLat / count,
      lng: totalLng / count
    };
  };

  // Upload fleet from Excel/CSV
  const importFleetFromExcelCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      let fleet: FleetSettings;
      
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        fleet = await parseExcelFileForSimulation(file);
      } else if (file.name.toLowerCase().endsWith('.csv')) {
        fleet = await parseCSVFileForSimulation(file);
      } else {
        setError(t('setup.vehicleCreator.errors.invalidFile'));
        return;
      }

      // Validate the imported fleet
      if (fleet.vehicles.length === 0) {
        setError(t('setup.vehicleCreator.errors.noVehicleData'));
        return;
      }
      
      // Update the vehicles state and call onFleetCreated
      setVehicles(fleet.vehicles);
      setError(null);
      
      // Calculate total vehicles for display
      const totalVehicles = fleet.vehicles.reduce((sum, v) => sum + v.number_of_vehicles, 0);
      
      // Auto-position imported vehicles if ride data is available
      if (rides.length > 0) {
        const centroid = calculateRideCentroid();
        const rideDate = rides[0]?.request_time
          ? extractServiceDate(rides[0].request_time)
          : DEFAULT_SIMULATION_DATE;
        
        const positionedFleet: FleetSettings = {
          vehicles: fleet.vehicles.map(vehicle => ({
            ...vehicle,
            vehicle: {
              ...vehicle.vehicle,
              shifts: vehicle.vehicle.shifts.map(shift => ({
                ...shift,
                start_address: { latitude: centroid.lat, longitude: centroid.lng },
                end_address: { latitude: centroid.lat, longitude: centroid.lng },
                start_time: combineDateWithTime(rideDate, (shift.start_time.split(' ')[1] || '06:00:00').slice(0, 5)),
                end_time: combineDateWithTime(rideDate, (shift.end_time.split(' ')[1] || '22:00:00').slice(0, 5))
              }))
            }
          }))
        };
        
        setVehicles(positionedFleet.vehicles);
        onFleetCreated(positionedFleet);
      } else {
        onFleetCreated(fleet);
      }
      
      // Fleet successfully imported - no modal needed, user will see vehicles appear in the UI
    } catch (error) {
      console.error('Error importing fleet from Excel/CSV:', error);
      setError(t('setup.vehicleCreator.errors.importError', { message: error instanceof Error ? error.message : 'Unknown error' }));
    }
    
    // Reset input
    event.target.value = '';
  };

  // Auto-position fleet near ride locations
  const autoPositionFleet = () => {
    const centroid = calculateRideCentroid();
    
    // Get the date from rides if available
    const rideDate = rides.length > 0 && rides[0]?.request_time
      ? extractServiceDate(rides[0].request_time)
      : DEFAULT_SIMULATION_DATE;
    
    // Update vehicle form with centroid coordinates
    setVehicleForm(prev => ({
      ...prev,
      startLat: centroid.lat,
      startLng: centroid.lng,
      endLat: centroid.lat,
      endLng: centroid.lng
    }));

    // Also update existing vehicles in the fleet
    if (vehicles.length > 0) {
      const updatedVehicles = vehicles.map(vehicleSetting => ({
        ...vehicleSetting,
        vehicle: {
          ...vehicleSetting.vehicle,
          shifts: vehicleSetting.vehicle.shifts.map(shift => {
            // Extract time from existing shift times
            const startTime = (shift.start_time.split(' ')[1] || '06:00:00').slice(0, 5);
            const endTime = (shift.end_time.split(' ')[1] || '22:00:00').slice(0, 5);

            return {
              ...shift,
              start_address: {
                latitude: centroid.lat + (Math.random() - 0.5) * 0.01,
                longitude: centroid.lng + (Math.random() - 0.5) * 0.01
              },
              end_address: {
                latitude: centroid.lat + (Math.random() - 0.5) * 0.01,
                longitude: centroid.lng + (Math.random() - 0.5) * 0.01
              },
              start_time: combineDateWithTime(rideDate, startTime),
              end_time: combineDateWithTime(rideDate, endTime)
            };
          })
        }
      }));
      
      setVehicles(updatedVehicles);
      onFleetCreated({ vehicles: updatedVehicles });
    }
  };

  // Auto-position fleet when rides are loaded
  useEffect(() => {
    if (rides.length > 0) {
      autoPositionFleet();
      
      // Analyze trips for intelligent suggestions
      const analysis = analyzeTripsPerHour(rides);
      setTripAnalysis(analysis);
      
      // Check if we should suggest wheelchair vehicles
      let hasWheelchairPassengers = false;
      rides.forEach(ride => {
        if (ride.passengers) {
          ride.passengers.forEach((passenger: any) => {
            if (passenger.type === 'wheelchair') {
              hasWheelchairPassengers = true;
            }
          });
        }
      });
      
      if (hasWheelchairPassengers) {
        setVehicleForm(prev => ({
          ...prev,
          vehicleType: 'wheelchair',
          wheelchairSeats: 1
        }));
      }
    }
  }, [rides]);

  const handleFormChange = (field: keyof VehicleForm, value: any) => {
    setVehicleForm(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const validateForm = (): boolean => {
    if (vehicleForm.numberOfVehicles < 1) {
      setError(t('setup.vehicleCreator.errors.numberOfVehicles'));
      return false;
    }
    if (vehicleForm.standardSeats < 0 || vehicleForm.wheelchairSeats < 0) {
      setError(t('setup.vehicleCreator.errors.negativeSeats'));
      return false;
    }
    if (vehicleForm.standardSeats === 0 && vehicleForm.wheelchairSeats === 0) {
      setError(t('setup.vehicleCreator.errors.noSeats'));
      return false;
    }
    if (Math.abs(vehicleForm.startLat) > 90 || Math.abs(vehicleForm.endLat) > 90) {
      setError(t('setup.vehicleCreator.errors.latitudeRange'));
      return false;
    }
    if (Math.abs(vehicleForm.startLng) > 180 || Math.abs(vehicleForm.endLng) > 180) {
      setError(t('setup.vehicleCreator.errors.longitudeRange'));
      return false;
    }
    return true;
  };

  const addVehicle = () => {
    if (!validateForm()) return;

    // Get the date from rides if available, otherwise use default
    const rideDate = rides.length > 0 && rides[0]?.request_time
      ? extractServiceDate(rides[0].request_time)
      : DEFAULT_SIMULATION_DATE;

    const newVehicle: VehicleSettings = {
      number_of_vehicles: vehicleForm.numberOfVehicles,
      vehicle: {
        capacity: [
          { type: "standard", number: vehicleForm.standardSeats },
          { type: "wheelchair", number: vehicleForm.wheelchairSeats }
        ],
        brand: vehicleForm.brand,
        vehicle_type: vehicleForm.vehicleType,
        shifts: [
          {
            start_address: {
              latitude: vehicleForm.startLat,
              longitude: vehicleForm.startLng
            },
            end_address: {
              latitude: vehicleForm.endLat,
              longitude: vehicleForm.endLng
            },
            start_time: combineDateWithTime(rideDate, vehicleForm.startTime),
            end_time: combineDateWithTime(rideDate, vehicleForm.endTime)
          }
        ]
      }
    };

    const updatedVehicles = [...vehicles, newVehicle];
    setVehicles(updatedVehicles);
    
    const newFleet: FleetSettings = {
      vehicles: updatedVehicles
    };
    
    onFleetCreated(newFleet);
    setError(null);
  };

  const removeVehicle = (index: number) => {
    const updatedVehicles = vehicles.filter((_, i) => i !== index);
    setVehicles(updatedVehicles);
    
    const newFleet: FleetSettings = {
      vehicles: updatedVehicles
    };
    
    onFleetCreated(newFleet);
  };

  const generateIntelligentFleetHandler = () => {
    if (rides.length === 0) {
      setError(t('setup.vehicleCreator.errors.noRidesForIntelligent'));
      return;
    }
    
    const intelligentFleet = generateIntelligentFleet(rides); // Generate based on ride analysis
    setVehicles(intelligentFleet.vehicles);
    onFleetCreated(intelligentFleet);
  };

  const generateFleetWithPresetHandler = (presetType: FleetPresetType) => {
    if (rides.length === 0) {
      setError(t('setup.vehicleCreator.errors.noRidesForPreset'));
      return;
    }
    
    const presetFleet = generateFleetWithPreset(rides, presetType);
    setVehicles(presetFleet.vehicles);
    onFleetCreated(presetFleet);
  };

  const getTotalVehicles = () => {
    return vehicles.reduce((sum, v) => sum + v.number_of_vehicles, 0);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('setup.vehicleCreator.title')}
        </Typography>

        {rides.length > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{t('setup.vehicleCreator.autoPositioned')}</strong> {t('setup.vehicleCreator.autoPositionedMessage', { count: rides.length })}
            </Typography>
          </Alert>
        )}

        {/* Trip Analysis Preview */}
        {tripAnalysis && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Icon size={20} d={ICON_FALLBACKS.Analytics} />
              <Typography variant="subtitle2" fontWeight="bold">
                {t('setup.vehicleCreator.tripAnalysisPreview')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.vehicleCreator.peakDemand')}
                </Typography>
                <Typography variant="h6" color="primary">
                  {t('setup.vehicleCreator.peakDemandValue', { count: tripAnalysis.maxTripsPerHour })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.vehicleCreator.suggestedVehicles')}
                </Typography>
                <Typography variant="h6" color="secondary">
                  {Math.max(1, Math.ceil(tripAnalysis.maxTripsPerHour / 3))}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('setup.vehicleCreator.peakHour')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {Object.entries(tripAnalysis.hourlyDistribution).find(([_, count]) => count === tripAnalysis.maxTripsPerHour)?.[0] || 'N/A'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {rides.length > 0 && vehicles.length > 0 && (
          (() => {
            const rideDate = rides[0]?.request_time ? extractServiceDate(rides[0].request_time) : null;
            const vehicleDate = vehicles[0]?.vehicle?.shifts?.[0]?.start_time?.split(' ')[0] || null;
            
            if (rideDate && vehicleDate && rideDate !== vehicleDate) {
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>{t('setup.vehicleCreator.dateMismatchWarning')}</strong> {t('setup.vehicleCreator.dateMismatchMessage', { rideDate, vehicleDate })}
                  </Typography>
                </Alert>
              );
            }
            return null;
          })()
        )}

        {rides.length > 0 && vehicles.length > 0 && (
          (() => {
            // Check for wheelchair accessibility
            let hasWheelchairPassengers = false;
            let totalWheelchairPassengers = 0;
            
            rides.forEach(ride => {
              if (ride.passengers) {
                ride.passengers.forEach((passenger: any) => {
                  if (passenger.type === 'wheelchair') {
                    hasWheelchairPassengers = true;
                    totalWheelchairPassengers += passenger.number_of_passengers || 0;
                  }
                });
              }
            });
            
            if (hasWheelchairPassengers) {
              let totalWheelchairCapacity = 0;
              vehicles.forEach(vehicleSetting => {
                vehicleSetting.vehicle.capacity.forEach((capacity: any) => {
                  if (capacity.type === 'wheelchair') {
                    totalWheelchairCapacity += capacity.number * vehicleSetting.number_of_vehicles;
                  }
                });
              });
              
              if (totalWheelchairCapacity < totalWheelchairPassengers) {
                return (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>{t('setup.vehicleCreator.wheelchairAccessibility')}</strong> {t('setup.vehicleCreator.wheelchairAccessibilityMessage', { needed: totalWheelchairPassengers, available: totalWheelchairCapacity })}
                    </Typography>
                  </Alert>
                );
              }
            }
            return null;
          })()
        )}

        {/* Intelligent Fleet Generation Info */}
        {rides.length > 0 && fleet && fleet.vehicles.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{t('setup.vehicleCreator.intelligentFleetGenerated')}</strong> {t('setup.vehicleCreator.intelligentFleetMessage', { count: rides.length, total: fleet.vehicles.reduce((sum, v) => sum + v.number_of_vehicles, 0) })}
            </Typography>
          </Alert>
        )}

        {/* Fleet Presets Section */}
        {onFleetPresetSelectionChange && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('setup.vehicleCreator.fleetPresets')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('setup.vehicleCreator.fleetPresetsDescription')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {FLEET_PRESETS.map((preset) => (
                <Button
                  key={preset.type}
                  variant={selectedFleetPresets.includes(preset.type) ? 'contained' : 'outlined'}
                  onClick={() => toggleFleetPreset(preset.type)}
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  {preset.name}
                  {preset.vehicleAdjustment !== 0 && (
                    <Chip 
                      label={preset.vehicleAdjustment > 0 ? `+${preset.vehicleAdjustment}` : `${preset.vehicleAdjustment}`}
                      size="small" 
                      sx={{ ml: 1, height: '20px' }}
                      color={
                        preset.type === 'auto' ? 'default' : 
                        preset.type === 'savvy' ? 'success' : 'primary'
                      }
                    />
                  )}
                </Button>
              ))}
            </Box>
            {selectedFleetPresets.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('setup.vehicleCreator.presetsSelected', { count: selectedFleetPresets.length })}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Vehicle Form */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('setup.vehicleCreator.addVehicle')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('setup.vehicleCreator.vehicleType')}</InputLabel>
                <Select
                  value={vehicleForm.vehicleType}
                  onChange={(e) => handleFormChange('vehicleType', e.target.value)}
                  label={t('setup.vehicleCreator.vehicleType')}
                >
                  <MenuItem value="standard">{t('setup.vehicleCreator.vehicleTypeStandard')}</MenuItem>
                  <MenuItem value="wheelchair">{t('setup.vehicleCreator.vehicleTypeWheelchair')}</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label={t('setup.vehicleCreator.standardSeats')}
                    type="number"
                    value={vehicleForm.standardSeats}
                    onChange={(e) => handleFormChange('standardSeats', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0, max: 20 }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label={t('setup.vehicleCreator.wheelchairSeats')}
                    type="number"
                    value={vehicleForm.wheelchairSeats}
                    onChange={(e) => handleFormChange('wheelchairSeats', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0, max: 5 }}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label={t('setup.vehicleCreator.numberOfVehicles')}
                type="number"
                value={vehicleForm.numberOfVehicles}
                onChange={(e) => handleFormChange('numberOfVehicles', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 50 }}
              />

              <TextField
                fullWidth
                label={t('setup.vehicleCreator.brand')}
                value={vehicleForm.brand}
                onChange={(e) => handleFormChange('brand', e.target.value)}
              />

              <Typography variant="subtitle2">{t('setup.vehicleCreator.startDepot')}</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label={t('setup.vehicleCreator.latitude')}
                    type="number"
                    value={vehicleForm.startLat}
                    onChange={(e) => handleFormChange('startLat', parseFloat(e.target.value) || 0)}
                    inputProps={{ step: 0.000001 }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label={t('setup.vehicleCreator.longitude')}
                    type="number"
                    value={vehicleForm.startLng}
                    onChange={(e) => handleFormChange('startLng', parseFloat(e.target.value) || 0)}
                    inputProps={{ step: 0.000001 }}
                  />
                </Box>
              </Box>

              <Typography variant="subtitle2">{t('setup.vehicleCreator.operatingHours')}</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label={t('setup.vehicleCreator.startTime')}
                    type="time"
                    value={vehicleForm.startTime}
                    onChange={(e) => handleFormChange('startTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ lang: 'en-GB' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label={t('setup.vehicleCreator.endTime')}
                    type="time"
                    value={vehicleForm.endTime}
                    onChange={(e) => handleFormChange('endTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ lang: 'en-GB' }}
                  />
                </Box>
              </Box>

              {/* Trip Analysis Preview */}
              {rides.length > 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('setup.vehicleCreator.tripAnalysisPreview')}
                  </Typography>
                  {(() => {
                    const analysis = analyzeTripsPerHour(rides);
                    const maxTripsPerHour = analysis.maxTripsPerHour;
                    const suggestedVehicles = Math.max(1, Math.ceil(maxTripsPerHour / 3));
                    const peakHour = Object.entries(analysis.hourlyDistribution).find(([_, count]) => count === maxTripsPerHour)?.[0];

                    return (
                      <Typography variant="body2" color="text.secondary">
                        <Trans
                          i18nKey="setup.vehicleCreator.peakDemandLine"
                          ns="simulation"
                          values={{ count: maxTripsPerHour, hour: peakHour, vehicles: suggestedVehicles }}
                          components={{ strong: <strong /> }}
                        />
                      </Typography>
                    );
                  })()}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<Icon size={20} name="plus" />}
                    onClick={addVehicle}
                    fullWidth
                  >
                    {t('setup.vehicleCreator.addVehicle')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={generateIntelligentFleetHandler}
                    disabled={rides.length === 0}
                  >
                    {t('setup.vehicleCreator.generateIntelligentFleet')}
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Icon size={20} name="upload" />}
                  component="label"
                  color="secondary"
                  fullWidth
                >
                  {t('setup.vehicleCreator.uploadFleet')}
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    hidden
                    onChange={importFleetFromExcelCSV}
                  />
                </Button>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{t('setup.vehicleCreator.uploadFormat')}</Typography>
                  <Typography variant="caption" display="block">
                    {t('setup.vehicleCreator.uploadColumns')}
                  </Typography>
                </Alert>
              </Box>
            </Box>
          </Box>

          {/* Vehicle List */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('setup.vehicleCreator.fleetSummary')}
              {vehicles.length > 0 && (
                <Chip
                  label={t('setup.vehicleCreator.vehiclesChip', { count: getTotalVehicles() })}
                  color="primary"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>

            {vehicles.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Icon size={48} name="bus" style={{ marginBottom: 8 }} />
                <Typography>{t('setup.vehicleCreator.noVehicles')}</Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {vehicles.map((vehicleSettings, index) => {
                  const vehicle = vehicleSettings.vehicle;
                  const standardSeats = vehicle.capacity.find(c => c.type === 'standard')?.number || 0;
                  const wheelchairSeats = vehicle.capacity.find(c => c.type === 'wheelchair')?.number || 0;
                  
                  return (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={t('setup.vehicleCreator.vehicleLabel', { count: vehicleSettings.number_of_vehicles, brand: vehicle.brand, type: vehicle.vehicle_type })}
                        secondary={t('setup.vehicleCreator.vehicleSeats', { standard: standardSeats, wheelchair: wheelchairSeats, start: vehicle.shifts[0]?.start_time.split(' ')[1], end: vehicle.shifts[0]?.end_time.split(' ')[1] })}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => removeVehicle(index)}
                          color="error"
                        >
                          <Icon size={20} name="trash" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
